/**
 * Der HausMeter-Client.
 *
 * Die Typen unter `generated/` stammen aus der OpenAPI-Spezifikation und werden
 * nicht von Hand bearbeitet. Dieses Modul legt nur das darum, was jeder Aufrufer
 * sonst selbst bauen müsste: Token anhängen, Account wählen, Fehler auspacken,
 * abgelaufene Sitzungen auffrischen.
 */
import createOpenApiClient, { type Middleware } from "openapi-fetch";
import type { paths } from "./generated/schema.js";

export interface HausMeterClientOptions {
  /** Basisadresse einschließlich `/api/v1`. */
  readonly baseUrl: string;
  /**
   * Liefert den aktuellen Zugriffstoken oder `null`, wenn niemand angemeldet ist.
   * Als Funktion, damit ein aufgefrischter Token sofort greift — und **darf
   * warten**: Wer den Token vor dem Absenden erst auffrischen muss (HM-098),
   * gibt eine Promise zurück, und die Anfrage geht erst danach hinaus. Die
   * Anfrage kommt mit, damit die Auffrischung selbst nicht auf sich wartet.
   */
  readonly getAccessToken?: (request: Request) => string | null | Promise<string | null>;
  /**
   * Account, auf den sich die Anfragen beziehen. Nötig, sobald ein Nutzer zu
   * mehreren Accounts gehört — sonst entscheidet der Server.
   */
  readonly getAccountId?: () => string | null;
  /** Wird bei `401` aufgerufen, damit die App die Sitzung auffrischen oder abmelden kann. */
  readonly onUnauthorized?: () => void | Promise<void>;
  /**
   * Schickt Cookies mit, auch an einen anderen Ursprung.
   *
   * Nötig für den Browser: Der Auffrischungstoken liegt seit ADR 0015 im
   * `HttpOnly`-Cookie `hm_refresh`, und ohne `credentials: "include"` sendet der
   * Browser es nicht an die API — in der Entwicklung liegt die Web-App auf Port
   * 5173, die API auf 3000. Für Mobile bleibt es aus: dort gibt es kein Cookie.
   */
  readonly includeCredentials?: boolean;
  /**
   * Liefert den Wert des lesbaren Cookies `hm_csrf`, der als `X-CSRF-Token`
   * zurückgeschickt wird (Double Submit). Als Funktion, weil er sich bei jeder
   * Rotation ändert.
   */
  readonly getCsrfToken?: () => string | null;
  /** Eigene `fetch`-Implementierung, z. B. für Tests oder React Native. */
  readonly fetch?: typeof globalThis.fetch;
}

export type HausMeterClient = ReturnType<typeof createHausMeterClient>;

export function createHausMeterClient(options: HausMeterClientOptions) {
  const client = createOpenApiClient<paths>({
    baseUrl: options.baseUrl,
    /**
     * Als Vorgabe der Anfrage und nicht in der Middleware: `credentials` lässt
     * sich an einem bereits gebauten `Request` nicht mehr ändern.
     */
    ...(options.includeCredentials ? { credentials: "include" as const } : {}),
    ...(options.fetch ? { fetch: options.fetch } : {}),
  });

  const auth: Middleware = {
    async onRequest({ request }) {
      /**
       * Ein ausdrücklich gesetzter `Authorization`-Kopf gewinnt. Die
       * Sitzungsverwaltung lädt den Nutzer mit einem **frischen** Token, bevor
       * sie ihn im Speicher ablegt; nähme die Middleware hier den gespeicherten,
       * ginge ausgerechnet diese Anfrage mit dem alten hinaus.
       */
      if (!request.headers.has("Authorization")) {
        const token = await options.getAccessToken?.(request);
        if (token) request.headers.set("Authorization", `Bearer ${token}`);
      }

      const accountId = options.getAccountId?.();
      if (accountId) request.headers.set("X-Account-Id", accountId);

      const csrf = options.getCsrfToken?.();
      if (csrf) request.headers.set("X-CSRF-Token", csrf);

      return request;
    },
    /**
     * **Gibt nichts zurück**, denn die Antwort bleibt unverändert. `openapi-fetch`
     * prüft einen Rückgabewert mit `instanceof Response` — und unter React Native
     * stammt die Antwort aus Expos `fetch`, nicht aus der globalen Klasse. Wer sie
     * hier zurückreicht, lässt dort **jede** Anfrage mit „must return new
     * Response()" scheitern, nachdem der Server längst geantwortet hat (HM-151);
     * im Browser fällt das nie auf.
     */
    async onResponse({ response }) {
      if (response.status === 401 && !(await isCredentialCheck(response))) await options.onUnauthorized?.();
      return undefined;
    },
  };

  client.use(auth);
  return client;
}

/**
 * **Ein `401` mit `auth.invalid_credentials` ist keine verlorene Sitzung**
 * (HM-189). Die API antwortet so auf ein falsch eingetipptes Passwort — beim
 * Anmelden, beim Passwortwechsel, beim Löschen des Kontos. Bis dahin meldete der
 * Client jedes `401` als Ende der Sitzung, und wer sich beim bisherigen Passwort
 * vertippte, stand auf der Anmeldeseite statt vor der Meldung „stimmt nicht".
 *
 * Gelesen wird eine Kopie der Antwort: Der Aufrufer liest den Körper selbst.
 */
async function isCredentialCheck(response: Response): Promise<boolean> {
  try {
    const body: unknown = await response.clone().json();
    return isApiError(body) && body.error.code === "auth.invalid_credentials";
  } catch {
    return false;
  }
}

/**
 * Fehlerkörper der API. Bewusst hier wiederholt statt aus `@hausmeter/contracts`
 * importiert: der Client soll auch in einer Umgebung ohne Zod nutzbar sein
 * (React Native, Firmware-Werkzeuge).
 */
export interface ApiError {
  readonly code: string;
  readonly message: string;
  readonly details?: readonly { path?: readonly string[]; code: string; message: string }[];
  readonly traceId?: string;
}

/** Prüft, ob ein Fehlerkörper der API vorliegt. */
export function isApiError(value: unknown): value is { error: ApiError } {
  if (typeof value !== "object" || value === null) return false;
  const error = (value as { error?: unknown }).error;
  if (typeof error !== "object" || error === null) return false;
  return typeof (error as { code?: unknown }).code === "string";
}

/**
 * Holt den Fehlercode aus einer Antwort. Rückgabe `null` bedeutet: kein bekannter
 * API-Fehler — dann liegt ein Netzwerk- oder Serverproblem vor, und der Aufrufer
 * darf nicht auf einen Code prüfen.
 */
export function errorCodeOf(value: unknown): string | null {
  return isApiError(value) ? value.error.code : null;
}
